import { defineComponent, PropType, ref, watch } from "vue";

import ImageFallback from "../../../components/imageFallback.jsx";
import {
    getFormattedDate,
    getFormattedTime,
    getMemberAvatar,
    getMemberDisplayName,
    getMemberTag,
    Member,
    useFetch,
} from "../../../utils.js";
import type { TimeRangeParams } from "../timeRange.js";
import {
    createCommonVoiceSessionItems,
    createTimelineParticipants,
    getTimelineBounds,
    VoiceChannel,
    VoiceChannelSessionEntry,
    VoiceCommonSessionEntry,
    VoiceSessionListItem,
} from "./sessionModel.js";

export const VoiceSessionDetailsModal = defineComponent({
    name: "VoiceSessionDetailsModal",

    props: {
        sessions: {
            type: Array as PropType<VoiceSessionListItem[]>,
            required: true,
        },
        selectedSessionId: {
            type: String,
            required: true,
        },
        currentUserId: {
            type: String,
            required: true,
        },
        timeRange: {
            type: Object as PropType<TimeRangeParams>,
            required: true,
        },
        onSelectSession: {
            type: Function as PropType<(sessionId: string) => void>,
            required: true,
        },
        onClose: {
            type: Function as PropType<() => void>,
            required: true,
        },
    },

    setup(props) {
        const { fetch } = useFetch();
        const commonSessions = ref<VoiceSessionListItem[]>([]);
        const selectedCommonSessionId = ref<string | null>(null);
        const timelineParticipants = ref<VoiceSessionListItem["participants"]>([]);
        const loadingCommonSessions = ref(false);
        const loadingTimeline = ref(false);

        function getInitialSession() {
            return props.sessions.find((session) => session.id === props.selectedSessionId) ?? props.sessions[0] ?? null;
        }

        function getSubjectSession() {
            const initialSession = getInitialSession();

            if (!initialSession)
                return null;

            return initialSession;
        }

        function getSubjectMember(): Member | null {
            return getSubjectSession()?.subjectMember ?? null;
        }

        async function fetchChannels(channelIds: readonly string[]) {
            if (!channelIds.length)
                return new Map<string, VoiceChannel>();

            const response = await fetch("/api/channels", {
                method  : "POST",
                headers : { "Content-Type": "application/json" },
                body    : JSON.stringify([ ...new Set(channelIds) ]),
            });

            const channels = await response.json() as VoiceChannel[];
            return new Map(channels.map((channel) => [ String(channel.id), channel ]));
        }

        async function loadCommonSessions() {
            const initialSession = getInitialSession();

            if (!initialSession)
                return;

            loadingCommonSessions.value = true;
            timelineParticipants.value = [];

            try {
                if (initialSession.subjectUserId === props.currentUserId) {
                    const channels = await fetchChannels(
                        props.sessions
                            .map((session) => session.channelId)
                            .filter((channelId): channelId is string => !!channelId),
                    );

                    commonSessions.value = props.sessions.map((session) => {
                        const channel = session.channelId ? channels.get(session.channelId) : null;

                        return {
                            ...session,
                            title: channel ? `#${channel.name}` : session.title,
                        };
                    });
                    selectedCommonSessionId.value = initialSession.id;
                    await loadTimeline(getSelectedCommonSession());
                    return;
                }

                const params = new URLSearchParams({
                    userId      : props.currentUserId,
                    targetId    : initialSession.subjectUserId,
                    before      : String(props.timeRange.before),
                    after       : String(props.timeRange.after),
                    limit       : "100",
                });
                const response = await fetch(`/api/voice-interactions?${params.toString()}`);
                const payload = await response.json() as { result?: VoiceCommonSessionEntry[] };
                const entries = payload.result ?? [];
                const channels = await fetchChannels(entries.map((entry) => String(entry.interacting_channel)));
                const sessions = createCommonVoiceSessionItems(
                    entries,
                    channels,
                    initialSession.subjectUserId,
                    initialSession.subjectMember,
                );

                commonSessions.value = sessions;
                selectedCommonSessionId.value = findClosestSessionId(sessions, initialSession.endedAt);
                await loadTimeline(getSelectedCommonSession());

            } finally {
                loadingCommonSessions.value = false;
            }
        }

        function findClosestSessionId(sessions: readonly VoiceSessionListItem[], timestamp: number) {
            if (!sessions.length)
                return null;

            return [ ...sessions ]
                .sort((left, right) => Math.abs(left.endedAt - timestamp) - Math.abs(right.endedAt - timestamp))[0]!.id;
        }

        async function loadTimeline(session: VoiceSessionListItem | null) {
            if (!session?.channelId) {
                timelineParticipants.value = session?.participants ?? [];
                return;
            }

            loadingTimeline.value = true;

            try {
                const params = new URLSearchParams({
                    channelId   : session.channelId,
                    before      : String(session.endedAt),
                    after       : String(session.startedAt),
                });
                const response = await fetch(`/api/voice-interactions?${params.toString()}`);
                const payload = await response.json() as {
                    members?: [string, Member | null][];
                    result?: VoiceChannelSessionEntry[];
                };
                const members = new Map<string, Member>();

                for (const [ userId, member ] of payload.members ?? []) {
                    if (member)
                        members.set(String(userId), member);
                }

                timelineParticipants.value = createTimelineParticipants(
                    payload.result ?? [],
                    members,
                    session,
                    props.currentUserId,
                );

            } finally {
                loadingTimeline.value = false;
            }
        }

        watch(() => [ props.selectedSessionId, props.timeRange.key ], loadCommonSessions, { immediate: true });
        watch(selectedCommonSessionId, () => {
            void loadTimeline(getSelectedCommonSession());
        });

        function getSelectedCommonSession() {
            return commonSessions.value.find((session) => session.id === selectedCommonSessionId.value)
                ?? commonSessions.value[0]
                ?? getInitialSession();
        }

        function renderParticipantName(participant: VoiceSessionListItem["participants"][number]) {
            if (!participant.member)
                return participant.primary ? "Utilisateur actuel" : "Utilisateur inconnu";

            return getMemberDisplayName(participant.member);
        }

        function renderParticipantAvatar(participant: VoiceSessionListItem["participants"][number]) {
            return (
                <ImageFallback
                    class="voice-session-modal-avatar"
                    src={participant.member ? getMemberAvatar(participant.member) : undefined}
                    default="https://cdn.discordapp.com/embed/avatars/0.png"
                    alt={renderParticipantName(participant)}
                />
            );
        }

        function renderTimeline(session: VoiceSessionListItem) {
            const bounds = getTimelineBounds(session);
            const participants = timelineParticipants.value.length
                ? timelineParticipants.value
                : session.participants;

            return (
                <div class="voice-session-timeline">
                    {participants.map((participant) => {
                        const left = ((participant.startedAt - bounds.start) / bounds.duration) * 100;
                        const width = (participant.duration / bounds.duration) * 100;

                        return (
                            <div class="voice-session-timeline-row" key={`${session.id}:${participant.userId}`}>
                                <router-link to={`/users/${participant.userId}`} class="voice-session-timeline-user">
                                    {renderParticipantAvatar(participant)}
                                    <div>
                                        <b>{renderParticipantName(participant)}</b>
                                        <span>{participant.member ? getMemberTag(participant.member) : participant.userId}</span>
                                    </div>
                                </router-link>

                                <div class="voice-session-timeline-track">
                                    <div
                                        class={{
                                            "voice-session-timeline-bar": true,
                                            "voice-session-timeline-bar-primary": participant.primary,
                                            "voice-session-timeline-bar-subject": participant.userId === session.subjectUserId,
                                        }}
                                        style={{
                                            left    : `${Math.max(0, left)}%`,
                                            width   : `${Math.max(2, width)}%`,
                                        }}
                                        title={`${getFormattedDate(participant.startedAt)} → ${getFormattedDate(participant.endedAt)}`}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }

        return () => {
            const subjectMember = getSubjectMember();
            const selectedSession = getSelectedCommonSession();

            if (!selectedSession)
                return null;

            return (
                <div class="voice-session-modal-backdrop" onClick={(event: MouseEvent) => {
                    event.stopPropagation();
                    props.onClose();
                }}>
                    <div class="voice-session-modal" onClick={(event: MouseEvent) => event.stopPropagation()}>
                        <header class="voice-session-modal-header">
                            <div class="voice-session-modal-subject">
                                <ImageFallback
                                    class="voice-session-modal-subject-avatar"
                                    src={subjectMember ? getMemberAvatar(subjectMember) : undefined}
                                    default="https://cdn.discordapp.com/embed/avatars/0.png"
                                    alt={subjectMember ? getMemberDisplayName(subjectMember) : "Utilisateur"}
                                />
                                <div>
                                    <h2>
                                        Sessions avec {subjectMember ? getMemberDisplayName(subjectMember) : "cet utilisateur"}
                                    </h2>
                                    <p>
                                        {subjectMember ? getMemberTag(subjectMember) : selectedSession.subjectUserId}
                                    </p>
                                </div>
                            </div>

                            <button type="button" class="voice-session-icon-button" onClick={props.onClose}>
                                <span class="material-symbols-outlined">close</span>
                            </button>
                        </header>

                        <div class="voice-session-modal-body">
                            <aside class="voice-session-modal-list">
                                {loadingCommonSessions.value
                                    ? <div class="voice-session-modal-empty">Chargement des sessions communes…</div>
                                    : commonSessions.value.length
                                        ? commonSessions.value.map((session) => (
                                        <button
                                            type="button"
                                            class={{
                                                "voice-session-modal-list-item": true,
                                                active: session.id === selectedSession.id,
                                            }}
                                            onClick={() => {
                                                selectedCommonSessionId.value = session.id;
                                            }}
                                            key={session.id}
                                        >
                                            <b>{session.title}</b>
                                            <span>{getFormattedTime(session.duration)}</span>
                                            <small>{getFormattedDate(session.startedAt)}</small>
                                        </button>
                                        ))
                                        : <div class="voice-session-modal-empty">Aucune session commune trouvée.</div>}
                            </aside>

                            <div class="voice-session-modal-detail">
                                <div class="voice-session-modal-summary">
                                    <div>
                                        <span>Salon</span>
                                        <b>{selectedSession.title}</b>
                                    </div>
                                    <div>
                                        <span>Durée</span>
                                        <b>{getFormattedTime(selectedSession.duration)}</b>
                                    </div>
                                    <div>
                                        <span>Début</span>
                                        <b>{getFormattedDate(selectedSession.startedAt)}</b>
                                    </div>
                                </div>

                                {loadingTimeline.value
                                    ? <div class="voice-session-modal-empty">Chargement de la timeline…</div>
                                    : renderTimeline({
                                        ...selectedSession,
                                        participants: timelineParticipants.value.length
                                            ? timelineParticipants.value
                                            : selectedSession.participants,
                                    })}
                            </div>
                        </div>
                    </div>
                </div>
            );
        };
    },
});
